import { useSetAtom } from "jotai";

import { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { Input } from "@/components/ui/input";

import { Label } from "@/components/ui/label";

import { Separator } from "@/components/ui/separator";

import { GridPattern } from "@/components/ui/grid-pattern";

import {
  checkSetupStatus,
  getGoogleAuthUrl,
  setupFirstAdmin,
} from "../api/authApi";

import { authStatusAtom, authUserAtom } from "../atoms/authAtoms";

import { useAuth } from "../hooks/useAuth";

function Spinner() {
  return (
    <svg
      className="mr-2 h-4 w-4 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

type Mode = "signin" | "signup" | "2fa";

const LoginPage = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const { isAuthenticated, isLoading, error, signIn } = useAuth();

  const { t } = useTranslation("common");

  const setAuthUser = useSetAtom(authUserAtom);

  const setAuthStatus = useSetAtom(authStatusAtom);

  const [mode, setMode] = useState<Mode>("signin");

  const [needsSetup, setNeedsSetup] = useState(false);

  const [checking, setChecking] = useState(true);

  // Shared

  const [email, setEmail] = useState("");

  const [password, setPassword] = useState("");

  const [localError, setLocalError] = useState<string | null>(null);

  const [googleLoading, setGoogleLoading] = useState(false);

  // 2FA

  const [totpCode, setTotpCode] = useState("");

  const totpInputRef = useRef<HTMLInputElement>(null);

  // Signup

  const [firstName, setFirstName] = useState("");

  const [lastName, setLastName] = useState("");

  const [confirmPassword, setConfirmPassword] = useState("");

  const [setupLoading, setSetupLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/integrations", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    checkSetupStatus()

      .then(({ needsSetup: ns }) => {
        setNeedsSetup(ns);

        if (ns) setMode("signup");
      })

      .catch(() => {})

      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    const errorParam = searchParams.get("error");

    if (errorParam === "google_auth_failed") {
      setLocalError(
        t(
          "LOGIN.googleAuthFailed",

          "Google authentication failed. Make sure your Google account is linked to an admin account.",
        ),
      );
    }
  }, [searchParams, t]);

  const switchMode = (newMode: Mode) => {
    setMode(newMode);

    setLocalError(null);
  };

  // Sign in

  const handleSignIn = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setLocalError(null);

    try {
      const result = await signIn({ email: email.trim(), password });

      if ("requiresTwoFactor" in result) {
        setMode("2fa");

        setTotpCode("");

        setTimeout(() => totpInputRef.current?.focus(), 50);

        return;
      }

      const dest = result.user.twoFactorEnabled
        ? "/integrations"
        : "/recommend-2fa";

      navigate(dest, { replace: true });
    } catch (e) {
      const message = e instanceof Error ? e.message : t("LOGIN.error");

      setLocalError(message);
    }
  };

  // 2FA

  const handleTotpSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (totpCode.length !== 6) return;

    setLocalError(null);

    try {
      const result = await signIn({ email: email.trim(), password, totpCode });

      if ("requiresTwoFactor" in result) {
        setLocalError(
          t(
            "LOGIN.invalidTotp",

            "Invalid verification code. Please try again.",
          ),
        );

        setTotpCode("");

        return;
      }

      navigate("/integrations", { replace: true });
    } catch (e) {
      const message = e instanceof Error ? e.message : t("LOGIN.error");

      if (message.includes("401") || message.includes("verification")) {
        setLocalError(
          t(
            "LOGIN.invalidTotp",

            "Invalid verification code. Please try again.",
          ),
        );

        setTotpCode("");
      } else {
        setLocalError(message);
      }
    }
  };

  // Sign up

  const handleSignUp = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setLocalError(null);

    if (password.length < 8) {
      setLocalError(
        t("SETUP.passwordTooShort", "Password must be at least 8 characters"),
      );

      return;
    }

    if (password !== confirmPassword) {
      setLocalError(t("SETUP.passwordMismatch", "Passwords do not match"));

      return;
    }

    setSetupLoading(true);

    try {
      const session = await setupFirstAdmin({
        email: email.trim(),

        password,

        firstName: firstName.trim(),

        lastName: lastName.trim(),
      });

      // Backend set eriol_session_admin + eriol_csrf cookies on the response.

      setAuthUser(session.user);

      setAuthStatus("authenticated");

      navigate("/recommend-2fa", { replace: true });
    } catch {
      setLocalError(
        t("SETUP.error", "Failed to create admin account. Please try again."),
      );
    } finally {
      setSetupLoading(false);
    }
  };

  // Google

  const handleGoogle = async () => {
    setGoogleLoading(true);

    try {
      const state = mode === "signup" ? "setup" : undefined;

      const url = await getGoogleAuthUrl(state);

      window.location.href = url;
    } catch {
      setLocalError(
        t("LOGIN.googleAuthFailed", "Google authentication failed."),
      );

      setGoogleLoading(false);
    }
  };

  const errorMessage = localError ?? error;

  const formLoading = isLoading || setupLoading;

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <svg
          className="h-8 w-8 animate-spin text-primary"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden bg-background">
      <GridPattern
        width={40}
        height={40}
        className="[mask-image:radial-gradient(600px_circle_at_center,white,transparent)] [stroke:var(--primary)] [stroke-opacity:0.2]"
      />

      <Card className="relative z-10 w-full max-w-md">
        <CardHeader className="text-center">
          <img src="/logo.png" alt="Eriol" className="h-12 w-12 mx-auto mb-6" />
          {mode === "2fa" ? (
            <>
              <CardTitle className="text-2xl">
                {t("LOGIN.twoFactorTitle", "Two-factor authentication")}
              </CardTitle>
              <CardDescription>
                {t(
                  "LOGIN.twoFactorDescription",

                  "Enter the 6-digit code from your authenticator app to continue.",
                )}
              </CardDescription>
            </>
          ) : mode === "signup" ? (
            <>
              <CardTitle className="text-2xl">
                {t("SETUP.title", "Create admin account")}
              </CardTitle>
              <CardDescription>
                {t(
                  "SETUP.subtitle",

                  "Set up the first administrator account to access the backoffice.",
                )}
              </CardDescription>
            </>
          ) : (
            <>
              <CardTitle className="text-2xl">{t("LOGIN.submit")}</CardTitle>
              <CardDescription>{t("LOGIN.subtitle")}</CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent>
          {errorMessage && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 text-destructive p-3 mb-4 text-center text-sm">
              {errorMessage}
            </div>
          )}

          {/* 2FA Form */}
          {mode === "2fa" && (
            <>
              <form onSubmit={handleTotpSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="totp">
                    {t("LOGIN.verificationCode", "Verification code")}
                  </Label>
                  <Input
                    ref={totpInputRef}
                    id="totp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) =>
                      setTotpCode(e.target.value.replace(/\D/g, ""))
                    }
                    className="font-mono text-center text-lg tracking-[0.5em]"
                    autoComplete="one-time-code"
                    autoFocus
                    disabled={isLoading}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading || totpCode.length !== 6}
                >
                  {isLoading && <Spinner />}
                  {t("LOGIN.verifyAndLogin", "Verify & Sign in")}
                </Button>
              </form>
              <p className="text-center text-sm text-muted-foreground mt-6">
                <button
                  type="button"
                  onClick={() => switchMode("signin")}
                  className="text-primary hover:underline"
                >
                  {t("LOGIN.backToLogin", "Back to login")}
                </button>
              </p>
            </>
          )}

          {/* Sign Up Form */}
          {mode === "signup" && (
            <>
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">
                      {t("SETUP.firstName", "First name")}
                    </Label>
                    <Input
                      id="firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="John"
                      required
                      disabled={formLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">
                      {t("SETUP.lastName", "Last name")}
                    </Label>
                    <Input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Doe"
                      required
                      disabled={formLoading}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signupEmail">
                    {t("SETUP.email", "Email")}
                  </Label>
                  <Input
                    id="signupEmail"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@eriol.io"
                    autoComplete="email"
                    required
                    disabled={formLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signupPassword">
                    {t("SETUP.password", "Password")}
                  </Label>
                  <Input
                    id="signupPassword"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    required
                    disabled={formLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">
                    {t("SETUP.confirmPassword", "Confirm password")}
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    required
                    disabled={formLoading}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={formLoading || googleLoading}
                >
                  {setupLoading && <Spinner />}
                  {t("SETUP.submit", "Create account")}
                </Button>
              </form>

              <div className="relative my-6">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
                  {t("LOGIN.orContinueWith", "or continue with")}
                </span>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={handleGoogle}
                disabled={formLoading || googleLoading}
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                {t("SETUP.googleSetup", "Set up with Google")}
              </Button>

              <p className="text-center text-sm text-muted-foreground mt-6">
                {t("LOGIN.alreadyHaveAccount", "Already have an account?")}{" "}
                <button
                  type="button"
                  onClick={() => switchMode("signin")}
                  className="text-primary hover:underline"
                >
                  {t("LOGIN.signInLink", "Sign in")}
                </button>
              </p>
            </>
          )}

          {/* Sign In Form */}
          {mode === "signin" && (
            <>
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t("LOGIN.email")}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@eriol.io"
                    autoComplete="email"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">{t("LOGIN.password")}</Label>
                    <Link
                      to="/forgot-password"
                      className="text-sm text-primary hover:underline"
                    >
                      {t("LOGIN.forgotPassword")}
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    disabled={isLoading}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading || googleLoading}
                >
                  {isLoading && <Spinner />}
                  {t("LOGIN.submit")}
                </Button>
              </form>

              <div className="relative my-6">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
                  {t("LOGIN.orContinueWith", "or continue with")}
                </span>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={handleGoogle}
                disabled={isLoading || googleLoading}
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                {t("LOGIN.googleSignIn", "Sign in with Google")}
              </Button>

              {needsSetup && (
                <p className="text-center text-sm text-muted-foreground mt-6">
                  {t("LOGIN.noAccountYet", "No account yet?")}{" "}
                  <button
                    type="button"
                    onClick={() => switchMode("signup")}
                    className="text-primary hover:underline"
                  >
                    {t("LOGIN.createAccountLink", "Create one")}
                  </button>
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
